/**
 * Form Reset Utilities
 *
 * Centralized timing and management for form resets after donation submission.
 * Used across all donation form components to ensure consistent behavior.
 */
import { useCallback, useEffect, useRef } from 'react';

/**
 * Standard delay before resetting form after "Support" button click
 *
 * @constant {number} FORM_RESET_DELAY_MS
 *
 * Timing rationale:
 * - Gives the external CharityStack script time to process the DONATE message
 * - Allows the checkout iframe to open smoothly
 * - Prevents form state from being visible during transition
 * - User doesn't see the reset happening (checkout overlay covers it)
 *
 * @default 2500 milliseconds (2.5 seconds)
 */
export const FORM_RESET_DELAY_MS = 2500;

/**
 * Schedule a form reset with the standard delay
 *
 * @param {Function} resetCallback - The function to call when resetting the form
 * @param {Function} setTimeoutIdCallback - Optional callback to store the timeout ID for cleanup
 * @returns {number} The timeout ID (can be used with clearTimeout)
 *
 * @example
 * // In a form component
 * const handleDonateClick = () => {
 *   // Send DONATE message
 *   window.top.postMessage({ action: 'DONATE', ... }, '*');
 *
 *   // Schedule form reset
 *   const timeoutId = scheduleFormReset(resetFormState, setResetTimeoutId);
 * };
 *
 * @example
 * // Cleanup on unmount
 * useEffect(() => {
 *   return () => {
 *     if (resetTimeoutId) {
 *       clearTimeout(resetTimeoutId);
 *     }
 *   };
 * }, [resetTimeoutId]);
 */
export const scheduleFormReset = (resetCallback, setTimeoutIdCallback) => {
  // Notify overlay (if present) that checkout has been triggered so it can
  // hide the close button while the checkout iframe is visible.
  // Uses a DOM CustomEvent on the current window — OverlayElement listens for this.
  try {
    window.dispatchEvent(new CustomEvent('charitystack:checkout-triggered'));
  } catch (_) {
    // Swallow — CustomEvent may not exist in very old environments
  }

  const timeoutId = setTimeout(resetCallback, FORM_RESET_DELAY_MS);

  if (setTimeoutIdCallback) {
    setTimeoutIdCallback(timeoutId);
  }

  return timeoutId;
};

/**
 * Cancel a scheduled form reset
 *
 * @param {number} timeoutId - The timeout ID returned by scheduleFormReset
 *
 * @example
 * // Cancel reset if user exits checkout early
 * cancelFormReset(resetTimeoutId);
 */
export const cancelFormReset = (timeoutId) => {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
};

/**
 * Keep the ids of a form's scheduled resets and clear them when the form
 * unmounts, so a reset never runs against a form (or a test environment) that is
 * gone. Pass the returned callback as scheduleFormReset's second argument. Every
 * id is kept, so a second DONATE inside the delay behaves as before while mounted.
 *
 * @returns {Function} setTimeoutIdCallback for scheduleFormReset
 */
export const useFormResetTimeouts = () => {
  const timeoutIdsRef = useRef(new Set());

  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current;
    return () => {
      timeoutIds.forEach(cancelFormReset);
      timeoutIds.clear();
    };
  }, []);

  return useCallback((timeoutId) => {
    timeoutIdsRef.current.add(timeoutId);
  }, []);
};
